import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type {
  AtVirtualClinicGender,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import {
  AT_VIRTUAL_CLINIC_GENDER_DISPLAY_NAMES
} from '@bindoc-projects/virtual-clinic-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const AT_GENDER_CONDITION_ID = 'gender';
export const AtFilterConditionGenderPluginType: FilterConditionPluginSpecification<
  AtVirtualClinicGender[],
  AtFilterConditionGenderDto,
  AtGenderCondition
> = {
  type: 'genderAT',
  category: 'case',
  displayName: { de: 'Geschlecht' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: AtFilterConditionGenderDto,
  ): FilterConditionPluginImpl<AtVirtualClinicGender[]> {
    return new AtFilterConditionGenderPlugin(obj.gender);
  },
  fromCondition(
    jsonObj: AtGenderCondition,
  ): FilterConditionPluginImpl<AtVirtualClinicGender[]> {
    return new AtFilterConditionGenderPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<AtVirtualClinicGender[]> {
    return new AtFilterConditionGenderPlugin([]);
  },
};

export class AtFilterConditionGenderPlugin extends FilterConditionPluginImpl<
  AtVirtualClinicGender[],
  AtFilterConditionGenderDto
> {
  static override Specification: FilterConditionPluginSpecification<
    AtVirtualClinicGender[],
    AtFilterConditionGenderDto,
    AtGenderCondition
  > = AtFilterConditionGenderPluginType;

  constructor(private gender: AtVirtualClinicGender[]) {
    super(AtFilterConditionGenderPluginType.type);
  }

  clone(): AtFilterConditionGenderPlugin {
    return new AtFilterConditionGenderPlugin([...this.gender]);
  }

  override equals(other: AtFilterConditionGenderPlugin): boolean {
    return super.equals(other) && BdArrayUtil.equals(this.gender, other.gender);
  }

  override validationToText(): string {
    if (this.gender.length === 0) {
      return 'Mindestens ein Geschlecht auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new AtGenderCondition(this.gender, []);
  }

  toJSON(): AtFilterConditionGenderDto | null {
    return {
      type: this.type,
      gender: this.gender,
    };
  }

  toText(): string | null {
    return this.gender.length === 1
      ? 'ein Geschlecht'
      : `${this.gender.length} Geschlechter`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.gender.map(
      (g) => AT_VIRTUAL_CLINIC_GENDER_DISPLAY_NAMES[g] ?? g,
    );
    return { title, items };
  }

  toValue(): AtVirtualClinicGender[] {
    return this.gender;
  }
}

export class AtGenderCondition extends BdContentCondition<AtVirtualClinicGender> {
  constructor(
    includes: AtVirtualClinicGender[],
    excludes: AtVirtualClinicGender[],
  ) {
    super(AT_GENDER_CONDITION_ID, includes, excludes);
  }
}

interface AtFilterConditionGenderDto {
  type: string;
  gender: AtVirtualClinicGender[];
}
